import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { userPool } from "../auth/userPool";
import { clearTemplateReloginGate } from "../templateReloginGate";

const AuthContext = createContext(null);

const buildAnonymousState = () => ({
  authStatus: "anonymous",
  currentUser: null,
  currentUserKey: null,
  session: null,
});

let authSnapshot = {
  authStatus: "loading",
  currentUser: null,
  currentUserKey: null,
  session: null,
};

const listeners = new Set();

function notifyAuthState(nextState) {
  authSnapshot = nextState;
  listeners.forEach((listener) => listener(nextState));
}

function subscribeAuthState(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function buildAuthenticatedState(user, session) {
  return {
    authStatus: "authenticated",
    currentUser: user,
    currentUserKey: user.getUsername(),
    session,
  };
}

function getUserSession(user) {
  return new Promise((resolve, reject) => {
    user.getSession((error, session) => {
      if (error) {
        reject(error);
        return;
      }

      if (!session?.isValid()) {
        reject(new Error("Session is invalid"));
        return;
      }

      resolve(session);
    });
  });
}

export async function refreshAuthState() {
  const user = userPool.getCurrentUser();
  if (!user) {
    const anonymousState = buildAnonymousState();
    notifyAuthState(anonymousState);
    return anonymousState;
  }

  try {
    const session = await getUserSession(user);
    const authenticatedState = buildAuthenticatedState(user, session);
    notifyAuthState(authenticatedState);
    return authenticatedState;
  } catch (error) {
    console.warn("Failed to refresh auth state", error);
    user.signOut();
    const anonymousState = buildAnonymousState();
    notifyAuthState(anonymousState);
    return anonymousState;
  }
}

export function setAuthenticatedSession(user, session) {
  const authenticatedState = buildAuthenticatedState(user, session);
  notifyAuthState(authenticatedState);
  return authenticatedState;
}

export function signOutUser() {
  clearTemplateReloginGate();
  userPool.getCurrentUser()?.signOut();
  const anonymousState = buildAnonymousState();
  notifyAuthState(anonymousState);
  return anonymousState;
}

export async function getIdToken() {
  if (authSnapshot.authStatus === "authenticated" && authSnapshot.session?.isValid()) {
    return authSnapshot.session.getIdToken().getJwtToken();
  }

  const nextState = await refreshAuthState();
  if (nextState.authStatus !== "authenticated" || !nextState.session) {
    throw new Error("Not authenticated");
  }

  return nextState.session.getIdToken().getJwtToken();
}

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(authSnapshot);

  useEffect(() => subscribeAuthState(setAuthState), []);

  useEffect(() => {
    refreshAuthState();
  }, []);

  const value = useMemo(
    () => ({
      ...authState,
      isAuthenticated: authState.authStatus === "authenticated",
      refreshAuthState,
      setAuthenticatedSession,
      signOut: signOutUser,
      getIdToken,
    }),
    [authState]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return authContext;
}
